import {
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { useState } from "react";
import { TaskModel, TaskStatus } from "../Models/Models";
import { TASK_STATUSES } from "./tasksConstants";

type SetTasksFn = (updater: (prev: TaskModel[]) => TaskModel[]) => void;

export function useKanbanDrag(tasks: TaskModel[], setTasks: SetTasksFn, onPersist: (taskId: number, status: TaskStatus) => void) {
  const [draggingTask, setDraggingTask] = useState<TaskModel | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  function handleDragStart(event: DragStartEvent) {
    const task = tasks.find((t) => t.id === (event.active.id as number)) ?? null;
    setDraggingTask(task);
  }

  function handleDragOver(event: DragOverEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const taskId = active.id as number;
    const overIsColumn = TASK_STATUSES.includes(over.id as TaskStatus);
    const overTask = overIsColumn ? null : tasks.find((t) => t.id === (over.id as number));
    const destStatus: TaskStatus | null = overIsColumn
      ? (over.id as TaskStatus)
      : (overTask?.status ?? null);

    if (!destStatus) return;

    setTasks((prev) => {
      const activeTask = prev.find((t) => t.id === taskId);
      if (!activeTask || activeTask.status === destStatus) return prev;
      return prev.map((t) => (t.id === taskId ? { ...t, status: destStatus } : t));
    });
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    setDraggingTask(null);

    if (!over) return;

    const taskId = active.id as number;
    const activeTask = tasks.find((t) => t.id === taskId);
    if (!activeTask) return;

    const overIsColumn = TASK_STATUSES.includes(over.id as TaskStatus);
    const overTaskId = overIsColumn ? null : (over.id as number);
    const overTask = overTaskId ? tasks.find((t) => t.id === overTaskId) : null;

    // Compute destStatus from the drop target, not from the stale tasks snapshot,
    // because handleDragOver already mutated state but the re-render hasn't fired yet.
    const destStatus: TaskStatus = overIsColumn
      ? (over.id as TaskStatus)
      : (overTask?.status ?? activeTask.status);

    if (!overIsColumn && overTask && activeTask.status === overTask.status) {
      setTasks((prev) => {
        const list = [...prev];
        const colIdxs = list
          .map((t, i) => ({ t, i }))
          .filter(({ t }) => t.status === activeTask.status)
          .map(({ i }) => i);
        const colTasks = colIdxs.map((i) => list[i]);
        const oldIdx = colTasks.findIndex((t) => t.id === taskId);
        const newIdx = colTasks.findIndex((t) => t.id === overTaskId);
        const reordered = arrayMove(colTasks, oldIdx, newIdx);
        colIdxs.forEach((pos, i) => { list[pos] = reordered[i]; });
        return list;
      });
    }

    onPersist(taskId, destStatus);
  }

  return { draggingTask, sensors, handleDragStart, handleDragOver, handleDragEnd };
}
